import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { type CommunityTab, useCommunityTab } from '../hooks/useCommunityTab';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCommunity, useDeleteCommunity, useUpdateCommunity } from '../api/communities';
import { useCommunityMtos, useDeleteMto, useUploadCommunityCsv, useUploadMtos } from '../api/mtos';
import { useCreateManualMtoLinesBulk } from '../api/lines';
import { useBuilders } from '../api/builders';
import { AddManualLinesBulkModal } from '../components/mto/AddManualLineModal';
import { MtoQuantitiesModal } from '../components/mto/MtoQuantitiesModal';
import {
  useCommunitySuppliers,
  useSaveCommunitySuppliers,
  type CommunitySupplierSlot,
} from '../api/communitySuppliers';
import {
  useAddLaborRate,
  useLaborRateHistory,
  useLaborRates,
  type LaborRateRow,
} from '../api/communityLabor';
import { formatSupplierDate, todayIsoDate } from '../api/suppliers';
import { useCommunityUnpriced } from '../api/communityUnpriced';
import { useCustomerModels } from '../api/customers';
import { useModelAssignments, useAssignCommunityModel, useUnassignCommunityModel } from '../api/modelAssignments';
import { sortByDisplayName, sortByModelLabel } from '../lib/sortByName';
import { toastMtoPreserveResult } from '../lib/mtoPreserveSummary';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Button } from '../components/ui/Button';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { DropZone } from '../components/ui/DropZone';
import { Input } from '../components/ui/Input';
import { RichTextEditor } from '../components/ui/RichTextEditor';
import { Select } from '../components/ui/Select';
import { DataTable } from '../components/ui/DataTable';
import { Modal } from '../components/ui/Modal';
import type { ColumnDef } from '@tanstack/react-table';
import { downloadAuthorized } from '../api/client';
import { useCommunityProperties, useScopes } from '../api/masterTables';
import { ScopeCheckboxGroup } from '../components/community/ScopeCheckboxGroup';
import { useToast } from '../hooks/useToast';
import { useUnsavedChangesPrompt } from '../hooks/useUnsavedChangesWarning';
import { UnsavedChangesModal } from '../components/ui/UnsavedChangesModal';
import {
  normalizeQuoteFooterHtml,
  suppliersDraftDirty,
} from '../lib/unsavedFormState';
import { CommunityLaborAdvancedSections, type PricingSection } from './CommunityLaborAdvanced';
import { CommunityAuditTab } from './CommunityAuditTab';
import { AcceptedBidBanner } from '../components/community/AcceptedBidBanner';
import { useAuthState } from '../auth/AuthProvider';
import page from './Page.module.css';

const CommunityBidBuilder = lazy(() =>
  import('./BidPage').then((m) => ({ default: m.CommunityBidBuilder }))
);

export function CommunityPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const toast = useToast();
  const { data: community } = useCommunity(communityId);
  const { data: builders } = useBuilders();
  const builder = builders?.find((b) => String(b.id) === String(community?.builder_id));
  const { data: mtos, isLoading } = useCommunityMtos(communityId);
  const upload = useUploadMtos(communityId);
  const uploadCsv = useUploadCommunityCsv(communityId);
  const del = useDeleteMto();
  const createManualBulk = useCreateManualMtoLinesBulk(communityId);
  const [tab, setTab] = useCommunityTab(communityId);
  const [pendingMtoDelete, setPendingMtoDelete] = useState<{ mtoId: number; name: string } | null>(null);
  const [addManualLinesOpen, setAddManualLinesOpen] = useState(false);
  const [mtoQuantitiesOpen, setMtoQuantitiesOpen] = useState(false);
  const [overviewDirty, setOverviewDirty] = useState(false);
  const [suppliersDirty, setSuppliersDirty] = useState(false);
  const [bidDirty, setBidDirty] = useState(false);
  const hasUnsaved = overviewDirty || suppliersDirty || bidDirty;
  const unsaved = useUnsavedChangesPrompt({ when: hasUnsaved });

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Builders', to: '/builders' },
          { label: builder?.name ?? 'Builder', to: builder ? `/builders/${builder.id}` : '/builders' },
          { label: community?.name ?? `Community ${communityId}` },
        ]}
      />

      <h1 className={page.title} style={{ fontSize: 22 }}>
        {community?.name?.trim() ? community.name : communityId ? `Community ${communityId}` : 'Community'}
      </h1>

      {community?.acceptedBid ? <AcceptedBidBanner acceptedBid={community.acceptedBid} /> : null}

      <div className={page.tabs}>
        {(
          [
            ['overview', 'Overview'],
            ['models', 'On-Site Models'],
            ['mtos', 'MTOs'],
            ['suppliers', 'Suppliers'],
            ['labor', 'Costs'],
            ['audit', 'Audit'],
            ['bid', 'Project Profitability'],
          ] as [CommunityTab, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={tab === id ? `${page.tab} ${page.tabActive}` : page.tab}
            onClick={() => unsaved.requestLeave(() => setTab(id))}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && community && communityId ? (
        <CommunityOverview
          key={communityId}
          community={community}
          communityId={communityId}
          builderId={String(community.builder_id)}
          bidLocked={!!community.bidLocked}
          toast={toast}
          onDirtyChange={setOverviewDirty}
        />
      ) : null}

      {tab === 'models' && communityId && community ? (
        <CommunityAssignmentsTab
          communityId={communityId}
          builderId={String(community.builder_id)}
          bidLocked={!!community.bidLocked}
          toast={toast}
        />
      ) : null}

      {tab === 'suppliers' && communityId && community ? (
        <CommunitySuppliersTab
          key={communityId}
          communityId={communityId}
          bidLocked={!!community.bidLocked}
          toast={toast}
          onDirtyChange={setSuppliersDirty}
        />
      ) : null}

      {tab === 'labor' && communityId && community ? (
        <CommunityLaborTab
          key={communityId}
          communityId={communityId}
          mtos={mtos ?? []}
          bidLocked={!!community.bidLocked}
          toast={toast}
        />
      ) : null}

      {tab === 'audit' && communityId && community ? (
        <CommunityAuditTab
          key={communityId}
          communityId={communityId}
          communityName={String(community.name ?? '')}
        />
      ) : null}

      {tab === 'bid' && communityId ? (
        <Suspense fallback={<p className={page.muted}>Loading project profitability…</p>}>
          <CommunityBidBuilder
            key={communityId}
            communityId={communityId}
            onDirtyChange={setBidDirty}
          />
        </Suspense>
      ) : null}

      <UnsavedChangesModal prompt={unsaved} />

      {tab === 'mtos' ? (
        <>
          {community?.bidLocked ? (
            <p className={page.muted} style={{ marginBottom: 'var(--space-md)' }}>
              MTO uploads and deletes are disabled while this community has an accepted bid.
            </p>
          ) : null}
          <div className={page.card}>
            <div
              className={page.row}
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-md)',
              }}
            >
              <h2 className={page.title} style={{ margin: 0 }}>
                MTO Files
              </h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="ghost"
                  disabled={!(mtos ?? []).length}
                  onClick={() => setMtoQuantitiesOpen(true)}
                >
                  View MTO Quantities
                </Button>
                <Button
                  variant="success"
                  disabled={!!community?.bidLocked || !(mtos ?? []).length}
                  onClick={() => setAddManualLinesOpen(true)}
                >
                  Add Manual Lines
                </Button>
                {communityId ? (
                  <Button
                    variant="success"
                    onClick={() =>
                      void downloadAuthorized(`/api/communities/${communityId}/export`).catch((e) =>
                        toast(e instanceof Error ? e.message : 'Export failed', true)
                      )
                    }
                  >
                    Export MTOs
                  </Button>
                ) : null}
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(280px, 1fr))',
                gap: 12,
                alignItems: 'stretch',
              }}
            >
              <DropZone
                multiple
                accept=".xlsx,.xls"
                onFiles={(files) => {
                  if (community?.bidLocked) {
                    toast('Community has an accepted bid — edits are locked', true);
                    return;
                  }
                  upload.mutate(files, {
                    onSuccess: (r) =>
                      toastMtoPreserveResult(toast, `Imported ${r.imported ?? 0} files`, r.preserveSummary),
                    onError: (e) => toast(e instanceof Error ? e.message : 'Upload failed', true),
                  });
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>MTO Workbooks</h3>
                <p style={{ margin: 0 }}>Drop .xlsx / .xls files or click to browse</p>
              </DropZone>
              <DropZone
                accept=".csv"
                onFiles={(files) => {
                  if (community?.bidLocked) {
                    toast('Community has an accepted bid — edits are locked', true);
                    return;
                  }
                  uploadCsv.mutate(files[0]!, {
                    onSuccess: (r) => toastMtoPreserveResult(toast, 'CSV processed', r.preserveSummary),
                    onError: (e) => toast(e instanceof Error ? e.message : 'CSV failed', true),
                  });
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Base House CSV</h3>
                <p style={{ margin: 0 }}>Single file — drop or click to browse</p>
              </DropZone>
            </div>
          </div>
          <div className={page.card}>
            {isLoading ? <p className={page.muted}>Loading…</p> : null}
            <div style={{ display: 'grid', gap: 10 }}>
              {(mtos ?? []).map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 14px',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                  }}
                >
                  <div>
                    <Link to={`/mtos/${m.id}/lines?communityId=${communityId}`}>
                      <strong>{m.name}</strong>
                    </Link>
                    <div className={page.muted}>
                      {Number(m.line_count ?? 0)} lines · {Number(m.plan_count ?? 0)} plans
                      {m.model_name ? ` · Model: ${m.model_name}` : ''}
                    </div>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={!!community?.bidLocked}
                    onClick={() =>
                      setPendingMtoDelete({ mtoId: Number(m.id), name: String(m.name ?? '') })
                    }
                  >
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
      <ConfirmModal
        open={pendingMtoDelete !== null}
        title="Delete MTO?"
        confirmLabel="Delete MTO"
        onCancel={() => setPendingMtoDelete(null)}
        onConfirm={() => {
          if (!pendingMtoDelete || !communityId) return;
          del.mutate(
            { mtoId: pendingMtoDelete.mtoId, communityId },
            {
              onSuccess: () => {
                toast('MTO deleted');
                setPendingMtoDelete(null);
              },
              onError: (e) => toast(e instanceof Error ? e.message : 'Delete failed', true),
            }
          );
        }}
        isPending={del.isPending}
      >
        <p style={{ margin: 0 }}>
          Delete MTO &quot;{pendingMtoDelete?.name ?? ''}&quot;? Lines and plans attached to this file will be removed.
        </p>
      </ConfirmModal>
      <AddManualLinesBulkModal
        open={addManualLinesOpen}
        onClose={() => setAddManualLinesOpen(false)}
        mtos={mtos ?? []}
        isPending={createManualBulk.isPending}
        onSubmit={async (mtoFileIds, body) => {
          try {
            const result = await createManualBulk.mutateAsync({
              mto_file_ids: mtoFileIds,
              ...body,
            });
            toast(
              `Manual line added to ${result.count} MTO${result.count === 1 ? '' : 's'}`
            );
            setAddManualLinesOpen(false);
          } catch (ex) {
            toast(ex instanceof Error ? ex.message : 'Save failed', true);
          }
        }}
      />
      <MtoQuantitiesModal
        open={mtoQuantitiesOpen}
        onClose={() => setMtoQuantitiesOpen(false)}
        communityId={communityId}
      />
    </div>
  );
}

function CommunityAssignmentsTab({
  communityId,
  builderId,
  bidLocked,
  toast,
}: {
  communityId: string;
  builderId: string;
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  const { data: assigned, isLoading } = useModelAssignments(communityId);
  const { data: allModels } = useCustomerModels(builderId);
  const assign = useAssignCommunityModel(communityId, builderId);
  const unassign = useUnassignCommunityModel(communityId, builderId);

  const assignedIds = useMemo(
    () => new Set((assigned ?? []).map((r) => Number(r.model_id))),
    [assigned]
  );
  const available = useMemo(
    () =>
      sortByDisplayName((allModels ?? []).filter((m) => !assignedIds.has(Number(m.id)))),
    [allModels, assignedIds]
  );
  const assignedSorted = useMemo(
    () => sortByModelLabel(assigned ?? []),
    [assigned]
  );

  return (
    <div className={page.card}>
      <h2 className={page.title}>On-Site Models</h2>
      <p className={page.muted}>
        Attach catalog models to this community. The system creates shadow MTOs and syncs model lines.
      </p>
      {isLoading ? <p className={page.muted}>Loading…</p> : null}
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(2, minmax(280px, 1fr))',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            padding: 12,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <p className={page.muted} style={{ margin: '0 0 8px' }}>
            Available Models ({available.length})
          </p>
          {available.length ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
              {available.map((m) => (
                <li key={String(m.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
                  <Button
                    variant="success"
                    size="sm"
                    type="button"
                    disabled={bidLocked || assign.isPending}
                    onClick={() =>
                      assign.mutate(Number(m.id), {
                        onSuccess: () => toast('Model linked to community'),
                        onError: (e) => toast(e instanceof Error ? e.message : 'Failed', true),
                      })
                    }
                  >
                    Add
                  </Button>
                  <span>
                    <strong>{String(m.name)}</strong>
                    {m.elevation ? <span className={page.muted}> · {String(m.elevation)}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={page.muted} style={{ margin: 0 }}>
              All catalog models are already linked.
            </p>
          )}
        </div>
        <div
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            padding: 12,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <p className={page.muted} style={{ margin: '0 0 8px' }}>
            Linked Models ({assignedSorted.length})
          </p>
          {assignedSorted.length ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
              {assignedSorted.map((row) => (
                <li
                  key={String(row.model_id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}
                >
                  <Button
                    variant="danger"
                    size="sm"
                    type="button"
                    disabled={bidLocked || unassign.isPending}
                    onClick={() =>
                      unassign.mutate(Number(row.model_id), {
                        onSuccess: () => toast('Removed from community'),
                        onError: (e) => toast(e instanceof Error ? e.message : 'Failed', true),
                      })
                    }
                  >
                    Remove
                  </Button>
                  <Link to={`/models/${row.model_id}`}>
                    <strong>{String(row.model_name ?? row.model_id)}</strong>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className={page.muted} style={{ margin: 0 }}>
              No catalog models linked yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CommunityOverview({
  community,
  communityId,
  builderId,
  bidLocked,
  toast,
  onDirtyChange,
}: {
  community: Record<string, unknown>;
  communityId: string;
  builderId: string;
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const navigate = useNavigate();
  const auth = useAuthState();
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin';
  const update = useUpdateCommunity(communityId, builderId);
  const del = useDeleteCommunity(builderId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: scopeRows } = useScopes();
  const { data: propertyRows } = useCommunityProperties();
  const [name, setName] = useState(String(community.name ?? '').trim());
  const [numLots, setNumLots] = useState(String(community.num_lots ?? ''));
  const [city, setCity] = useState(String(community.city ?? ''));
  const [zip, setZip] = useState(String(community.zip ?? ''));
  const [sageId, setSageId] = useState(String(community.sage_id ?? '').trim());
  const [communityPropertyId, setCommunityPropertyId] = useState(
    String(community.community_property_id ?? '')
  );
  const [scopeIds, setScopeIds] = useState<number[]>(() =>
    Array.isArray(community.scopes)
      ? (community.scopes as { id?: unknown }[])
          .map((s) => Number(s.id))
          .filter((id) => id > 0)
      : []
  );
  const [quoteFooterHtml, setQuoteFooterHtml] = useState(
    String(community.quote_footer_html ?? '')
  );
  const [footerEdited, setFooterEdited] = useState(false);
  // Compare against last-saved draft. TipTap HTML often differs cosmetically from
  // the sanitized value returned by the API, which would leave the form dirty.
  const [saved, setSaved] = useState(() => ({
    name: String(community.name ?? '').trim(),
    numLots: String(community.num_lots ?? ''),
    city: String(community.city ?? ''),
    zip: String(community.zip ?? ''),
    sageId: String(community.sage_id ?? '').trim(),
    communityPropertyId: String(community.community_property_id ?? ''),
    scopeIds: Array.isArray(community.scopes)
      ? (community.scopes as { id?: unknown }[])
          .map((s) => Number(s.id))
          .filter((id) => id > 0)
          .sort((a, b) => a - b)
      : [],
    quoteFooterHtml: normalizeQuoteFooterHtml(String(community.quote_footer_html ?? '')),
  }));

  const scopeOptions = useMemo(
    () =>
      (scopeRows ?? []).map((r) => ({
        id: Number(r.id),
        name: String(r.name ?? ''),
      })),
    [scopeRows]
  );
  const propertyOptions = useMemo(
    () =>
      (propertyRows ?? []).map((r) => ({
        id: Number(r.id),
        name: String(r.name ?? ''),
      })),
    [propertyRows]
  );
  const propertyDirty = communityPropertyId !== saved.communityPropertyId;
  const dirty = useMemo(() => {
    // Property can still change when bid-locked; other fields cannot.
    if (bidLocked) return propertyDirty;
    const draftScopes = [...scopeIds].sort((a, b) => a - b);
    const footerDirty =
      footerEdited &&
      normalizeQuoteFooterHtml(quoteFooterHtml) !== saved.quoteFooterHtml;
    return (
      name.trim() !== saved.name ||
      numLots !== saved.numLots ||
      city !== saved.city ||
      zip !== saved.zip ||
      sageId.trim() !== saved.sageId ||
      propertyDirty ||
      draftScopes.join(',') !== saved.scopeIds.join(',') ||
      footerDirty
    );
  }, [
    bidLocked,
    propertyDirty,
    name,
    numLots,
    city,
    zip,
    sageId,
    scopeIds,
    quoteFooterHtml,
    footerEdited,
    saved,
  ]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  return (
    <div className={page.card}>
      <h2 className={page.title}>Community Overview</h2>
      <form
        className={page.row}
        style={{ flexDirection: 'column', alignItems: 'stretch', maxWidth: 720 }}
        onSubmit={(e) => {
          e.preventDefault();
          const propertyId = communityPropertyId ? Number(communityPropertyId) : null;
          if (bidLocked) {
            if (!propertyDirty || !propertyId) {
              toast('Community has an accepted bid — edits are locked', true);
              return;
            }
            update.mutate(
              { community_property_id: propertyId },
              {
                onSuccess: () => {
                  setSaved((prev) => ({ ...prev, communityPropertyId }));
                  toast('Community saved');
                },
                onError: (err) => toast(err instanceof Error ? err.message : 'Save failed', true),
              }
            );
            return;
          }
          const nextSaved = {
            name: name.trim(),
            numLots,
            city,
            zip,
            sageId: sageId.trim(),
            communityPropertyId,
            scopeIds: [...scopeIds].sort((a, b) => a - b),
            quoteFooterHtml: normalizeQuoteFooterHtml(quoteFooterHtml),
          };
          update.mutate(
            {
              name: nextSaved.name,
              num_lots: numLots.trim() ? Number(numLots) : null,
              city: city.trim() || null,
              zip: zip.trim() || null,
              sage_id: sageId.trim() || null,
              community_property_id: propertyId,
              scope_ids: scopeIds,
              quote_footer_html: quoteFooterHtml,
            },
            {
              onSuccess: () => {
                setSaved(nextSaved);
                setFooterEdited(false);
                toast('Community saved');
              },
              onError: (err) => toast(err instanceof Error ? err.message : 'Save failed', true),
            }
          );
        }}
      >
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={bidLocked} />
        <Input label="Lots" value={numLots} onChange={(e) => setNumLots(e.target.value)} type="number" disabled={bidLocked} />
        <Input label="City" value={city} onChange={(e) => setCity(e.target.value)} disabled={bidLocked} />
        <Input label="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} disabled={bidLocked} />
        <Input
          label="Sage ID"
          value={sageId}
          onChange={(e) => setSageId(e.target.value)}
          disabled={bidLocked}
        />
        <Select
          label="Community Property"
          value={communityPropertyId}
          onChange={(e) => setCommunityPropertyId(e.target.value)}
          required
        >
          <option value="" disabled>
            Select…
          </option>
          {propertyOptions.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </Select>
        <ScopeCheckboxGroup
          options={scopeOptions}
          selectedIds={scopeIds}
          onChange={setScopeIds}
          disabled={bidLocked}
        />
        <RichTextEditor
          label="Quote Footer"
          hint="Shown at the bottom of PDF quotes from Project Profitability. Text size is preserved in the PDF."
          value={quoteFooterHtml}
          onChange={(html) => {
            setFooterEdited(true);
            setQuoteFooterHtml(html);
          }}
          disabled={bidLocked}
        />
        <Button type="submit" variant="success" disabled={!dirty || update.isPending}>
          Save Changes
        </Button>
      </form>
      <p className={page.muted} style={{ marginTop: 16 }}>
        Use the <strong>On-Site Models</strong> tab to attach catalog models to this community.
      </p>
      {isAdmin ? (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--color-border, #e0e0e0)' }}>
          <Button type="button" variant="danger" onClick={() => setConfirmDelete(true)}>
            Delete Community
          </Button>
          <p className={page.muted} style={{ marginTop: 8, marginBottom: 0 }}>
            Removes this community from the portal. Existing data is kept.
          </p>
        </div>
      ) : null}
      <ConfirmModal
        open={confirmDelete}
        title="Delete Community?"
        confirmLabel="Delete"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          del.mutate(Number(communityId), {
            onSuccess: () => {
              toast('Community deleted');
              setConfirmDelete(false);
              navigate(`/builders/${builderId}`);
            },
            onError: (err) => toast(err instanceof Error ? err.message : 'Delete failed', true),
          });
        }}
        isPending={del.isPending}
      >
        <p style={{ margin: 0 }}>
          Remove &quot;{name.trim() || 'this community'}&quot; from the portal? It will no longer appear in
          community lists. Existing data is kept.
        </p>
      </ConfirmModal>
    </div>
  );
}

function slotsToDraftMap(slots: CommunitySupplierSlot[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const slot of slots) {
    map[String(slot.sub_phase_id)] = slot.supplier ?? '';
  }
  return map;
}

function CommunitySuppliersTab({
  communityId,
  bidLocked,
  toast,
  onDirtyChange,
}: {
  communityId: string;
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { data, isLoading } = useCommunitySuppliers(communityId);
  const save = useSaveCommunitySuppliers(communityId);

  if (isLoading || !data) return <p className={page.muted}>Loading suppliers…</p>;

  return (
    <CommunitySuppliersEditor
      key={JSON.stringify(data.slots)}
      slots={data.slots}
      save={save}
      bidLocked={bidLocked}
      toast={toast}
      onDirtyChange={onDirtyChange}
    />
  );
}

function CommunitySuppliersEditor({
  slots,
  save,
  bidLocked,
  toast,
  onDirtyChange,
}: {
  slots: CommunitySupplierSlot[];
  save: ReturnType<typeof useSaveCommunitySuppliers>;
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const savedMap = useMemo(() => slotsToDraftMap(slots), [slots]);
  const [draft, setDraft] = useState(() => ({ ...savedMap }));
  const dirty = useMemo(
    () => suppliersDraftDirty(savedMap, draft, bidLocked),
    [savedMap, draft, bidLocked]
  );

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const byPhase = useMemo(() => {
    const groups: Array<{ phaseName: string; slots: CommunitySupplierSlot[] }> = [];
    const indexByPhase = new Map<string, number>();
    for (const slot of slots) {
      const key = `${slot.phase_id}:${slot.phase_name}`;
      let idx = indexByPhase.get(key);
      if (idx == null) {
        idx = groups.length;
        indexByPhase.set(key, idx);
        groups.push({ phaseName: slot.phase_name, slots: [] });
      }
      groups[idx]!.slots.push(slot);
    }
    return groups;
  }, [slots]);

  return (
    <div className={page.card}>
      <h2 className={page.title}>Supplier by Sub-Phase</h2>
      <p className={page.muted}>
        Pick one supplier per sub-phase that appears in payable task group mappings. Eligible suppliers are
        configured on Admin → Phases.
      </p>
      {!slots.length ? (
        <p className={page.muted}>
          No sub-phases are mapped from payable task groups yet. Map PTGs to sub-phases to assign suppliers.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 20, maxWidth: 560 }}>
          {byPhase.map((group) => (
            <div key={group.phaseName} style={{ display: 'grid', gap: 12 }}>
              <h3 className={page.title} style={{ fontSize: 15, margin: 0 }}>
                {group.phaseName}
              </h3>
              {group.slots.map((slot) => {
                const key = String(slot.sub_phase_id);
                const emptyAllowlist = Boolean(slot.error);
                return (
                  <div key={key}>
                    <Select
                      label={slot.sub_phase_name}
                      value={draft[key] ?? ''}
                      disabled={bidLocked || emptyAllowlist}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    >
                      <option value="">— None —</option>
                      {slot.eligible_suppliers.map((s) => (
                        <option key={s.id} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                    {emptyAllowlist ? (
                      <p style={{ color: 'var(--color-danger, #b91c1c)', fontSize: 13, margin: '4px 0 0' }}>
                        {slot.error}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      <Button
        variant="success"
        style={{ marginTop: 16 }}
        disabled={bidLocked || save.isPending || !slots.length}
        onClick={() =>
          save.mutate(draft, {
            onSuccess: () => toast('Suppliers saved'),
            onError: (e) => toast(e instanceof Error ? e.message : 'Save failed', true),
          })
        }
      >
        Save Suppliers
      </Button>
    </div>
  );
}

function CommunityLaborTab({
  communityId,
  mtos,
  bidLocked,
  toast,
}: {
  communityId: string;
  mtos: { id?: unknown; name?: unknown }[];
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  const { data: rates, isLoading } = useLaborRates(communityId);
  const { data: unpriced, isLoading: unpricedLoading } = useCommunityUnpriced(communityId);
  const [section, setSection] = useState<PricingSection>('laborDefaults');

  const unpricedItemCount = (unpriced?.defaultItems ?? []).filter((item) => item.trulyMissing).length;

  const sectionTabs: [PricingSection, string][] = [
    ['laborDefaults', 'Community Labor Rates'],
    ['unpricedItems', 'Missing Supplier Prices'],
    // ['mtoPriceOverrides', 'Per-Line Rate Overrides'],
    ['modelOverrides', 'Force Line Price & Qty'],
  ];

  return (
    <>
      <div className={page.tabs}>
        {sectionTabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={section === id ? `${page.tab} ${page.tabActive}` : page.tab}
            onClick={() => setSection(id)}
            style={
              id === 'unpricedItems'
                ? { display: 'inline-flex', alignItems: 'center', gap: 8 }
                : undefined
            }
          >
            {label}
            {id === 'unpricedItems' ? (
              <span
                aria-label={
                  unpricedLoading
                    ? 'Loading missing supplier price count'
                    : `${unpricedItemCount} ${unpricedItemCount === 1 ? 'item' : 'items'} truly missing supplier price`
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 22,
                  height: 22,
                  padding: '0 7px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  background: 'var(--color-danger)',
                  color: '#fff',
                  lineHeight: 1,
                }}
              >
                {unpricedLoading ? '…' : unpricedItemCount}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      {section === 'laborDefaults' ? (
        isLoading || rates === undefined ? (
          <p className={page.muted}>Loading costs…</p>
        ) : (
          <CommunityLaborEditor
            communityId={communityId}
            rates={rates}
            bidLocked={bidLocked}
            toast={toast}
          />
        )
      ) : (
        <CommunityLaborAdvancedSections
          communityId={communityId}
          mtos={mtos}
          section={section}
          bidLocked={bidLocked}
          toast={toast}
        />
      )}
    </>
  );
}

function LaborEditPriceForm({
  item,
  onClose,
  onSave,
  isPending,
}: {
  item: LaborRateRow;
  onClose: () => void;
  onSave: (payload: {
    item_name: string;
    unit_cost: number;
    effective_date: string;
    phase?: string | null;
    category?: string | null;
  }) => void;
  isPending: boolean;
}) {
  const toast = useToast();
  const [unitCost, setUnitCost] = useState(item.unit_cost != null ? String(item.unit_cost) : '');
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const unitCostNum = Number(unitCost);
        if (!Number.isFinite(unitCostNum)) {
          toast('Unit price required', true);
          return;
        }
        onSave({
          item_name: item.item_name,
          unit_cost: unitCostNum,
          effective_date: effectiveDate,
          phase: item.phase ?? null,
          category: item.category ?? null,
        });
      }}
    >
      <p className={page.muted} style={{ marginTop: 0 }}>
        Item: {item.item_name}
      </p>
      <Input
        label="Unit Price"
        type="number"
        step="0.01"
        value={unitCost}
        onChange={(e) => setUnitCost(e.target.value)}
        required
      />
      <Input
        label="Effective Date"
        type="date"
        value={effectiveDate}
        onChange={(e) => setEffectiveDate(e.target.value)}
        required
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={isPending}>
          Save Price
        </Button>
      </div>
    </form>
  );
}

function CommunityLaborEditor({
  communityId,
  rates,
  bidLocked,
  toast,
}: {
  communityId: string;
  rates: LaborRateRow[];
  bidLocked?: boolean;
  toast: (m: string, e?: boolean) => void;
}) {
  const addRate = useAddLaborRate(communityId);
  const [editingItem, setEditingItem] = useState<LaborRateRow | null>(null);
  const [historyItem, setHistoryItem] = useState<LaborRateRow | null>(null);

  const { data: rateHistory, isLoading: historyLoading } = useLaborRateHistory(
    communityId,
    historyItem?.item_name ?? null
  );

  async function submitPrice(payload: {
    item_name: string;
    unit_cost: number;
    effective_date: string;
    phase?: string | null;
    category?: string | null;
  }) {
    try {
      await addRate.mutateAsync(payload);
      toast('Price saved');
      setEditingItem(null);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save price', true);
    }
  }

  const laborRateSearchKeys = useMemo(
    () => ['item_name', 'phase', 'unit_cost'] as const satisfies ReadonlyArray<keyof LaborRateRow>,
    []
  );

  const cols = useMemo<ColumnDef<LaborRateRow>[]>(
    () => [
      {
        accessorKey: 'item_name',
        header: 'Item',
      },
      {
        accessorKey: 'phase',
        header: 'Phase',
        cell: ({ row }) => row.original.phase ?? '—',
      },
      {
        id: 'unit_cost',
        header: 'Current Unit Price',
        accessorKey: 'unit_cost',
        meta: { align: 'right' },
        cell: ({ row }) =>
          row.original.unit_cost != null
            ? Number(row.original.unit_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })
            : '—',
      },
      {
        id: 'effective_date',
        header: 'Effective Date',
        accessorKey: 'effective_date',
        cell: ({ row }) => formatSupplierDate(row.original.effective_date),
      },
      {
        id: 'updated_at',
        header: 'Updated',
        accessorKey: 'updated_at',
        cell: ({ row }) => formatSupplierDate(row.original.updated_at),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!bidLocked ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingItem(row.original)}>
                Edit Price
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" onClick={() => setHistoryItem(row.original)}>
              Price History
            </Button>
          </div>
        ),
      },
    ],
    [bidLocked]
  );

  return (
    <div className={page.card}>
      <h3 className={page.title} style={{ fontSize: 16, margin: '0 0 var(--space-md)' }}>
        Community Labor Rates
      </h3>
      <p className={page.muted}>
        Distinct labor items from the item master (Labor cost type) across all floorplans in this
        community. Current rates use the latest effective date on or before today. Use Edit Price to
        set a new unit price and effective date; lines without a supplier price or per-line override use
        these rates.
      </p>
      {rates.length ? (
        <DataTable
          data={rates}
          columns={cols}
          searchKeys={laborRateSearchKeys}
          searchPlaceholder="Item, phase, or unit price"
        />
      ) : (
        <p className={page.muted}>No matched labor items found in this community&apos;s floorplans yet.</p>
      )}

      <Modal
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        title={editingItem ? `Edit Price — ${editingItem.item_name}` : 'Edit Price'}
        showClose={false}
      >
        {editingItem ? (
          <LaborEditPriceForm
            key={editingItem.item_name}
            item={editingItem}
            onClose={() => setEditingItem(null)}
            isPending={addRate.isPending}
            onSave={(payload) => {
              void submitPrice(payload);
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={!!historyItem}
        onClose={() => setHistoryItem(null)}
        title={historyItem ? `Price History — ${historyItem.item_name}` : 'Price History'}
        showClose={false}
      >
        {historyLoading ? <p className={page.muted}>Loading…</p> : null}
        {!historyLoading && rateHistory?.length ? (
          <DataTable
            data={rateHistory}
            columns={[
              {
                header: 'Effective Date',
                accessorKey: 'effective_date',
                cell: ({ row }) => formatSupplierDate(row.original.effective_date),
              },
              {
                header: 'Unit Price',
                accessorKey: 'unit_cost',
                meta: { align: 'right' as const },
                cell: ({ row }) =>
                  row.original.unit_cost != null
                    ? Number(row.original.unit_cost).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })
                    : '—',
              },
              {
                header: 'Updated',
                accessorKey: 'updated_at',
                cell: ({ row }) => formatSupplierDate(row.original.updated_at),
              },
            ]}
            searchKeys={[
              (r) => formatSupplierDate(r.effective_date),
              (r) =>
                r.unit_cost != null
                  ? Number(r.unit_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })
                  : '',
              (r) => formatSupplierDate(r.updated_at),
            ]}
            searchPlaceholder="Date or price…"
          />
        ) : null}
        {!historyLoading && !rateHistory?.length ? (
          <p className={page.muted}>No rate history for this item.</p>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="button" variant="ghost" onClick={() => setHistoryItem(null)}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}
